import { isAxiosError } from "axios";

import { PillVariants } from "src/base-components/Pill";
import { CaseDeserialized } from "src/cases/queries/casesTypes";
import { CasePropertiesValue } from "src/clients/case-management";
import { toastActions } from "src/design-system/Toast/utils";
import { isPreconditionError } from "src/utils/predicates";

export const getPropertyVariant = (
  value: string,
  property?: CasePropertiesValue,
): PillVariants => {
  if (!value || !property) return "gray";

  if (property.type === "enum") {
    const enumValue = property.values?.find((v) => v.value === value);
    if (enumValue?.color) {
      return enumValue.color as PillVariants;
    }
  }
  return "gray";
};

export const getStatusVariant = (
  status: string,
  templateData?: CaseDeserialized["data"]["template_version"],
): PillVariants => {
  return getPropertyVariant(status, templateData?.case_properties?.status);
};

export const getPriorityVariant = (
  priority: string,
  templateData?: CaseDeserialized["data"]["template_version"],
): PillVariants => {
  return getPropertyVariant(priority, templateData?.case_properties?.priority);
};

export const onAssignCasesError = (
  error: unknown,
  toastOptions: {
    id: string;
    action: "claiming" | "assigning";
  },
) => {
  if (isAxiosError(error)) {
    toastActions.failure({
      id: toastOptions.id,
      title: `Error ${toastOptions.action} cases`,
      description: error.response?.data.errors[0]?.detail ?? "Unknown error",
    });
  } else {
    toastActions.failure({
      id: toastOptions.id,
      title: `Error ${toastOptions.action} cases`,
      description: `An error occurred while ${toastOptions.action} the cases. Please try again later.`,
    });
  }
};

export const onArchiveCasesError = (
  error: unknown,
  toastOptions?: { id?: string },
) => {
  if (isPreconditionError(error)) {
    toastActions.failure({
      id: toastOptions?.id,
      title: "Archiving failed",
      description:
        "An error occurred while archiving. Please refresh the page and try again.",
    });
    return;
  }

  if (isAxiosError(error)) {
    toastActions.failure({
      id: toastOptions?.id,
      title: "Archiving failed",
      description: error.response?.data.errors[0]?.detail ?? "Unknown error",
    });
    return;
  }

  toastActions.failure({
    id: toastOptions?.id,
    title: "Archiving failed",
    description:
      "Unexpected error occurred while archiving. Please try again later.",
  });
};

/**
 *
 * Type predicate to check if a property is the status property
 * @param property - property to check
 */
export const isStatusProperty = (
  property?: CasePropertiesValue,
): property is Extract<CasePropertiesValue, { type: "enum"; key: "status" }> =>
  !!property &&
  "key" in property &&
  property.key === "status" &&
  property.type === "enum";
